// The old dashboard's three-quarter arc, on its own — the drawing without any
// card around it.
//
// It was extracted from ArcGauge so a second card shape can use the same
// geometry. The Board's cards are not the VP page's: they carry the figure in
// a gauge AND a target, a change, a year to date and a bar per month, which is
// ExecMetricCard's shape rather than ArcGauge's. Copying the arc into a second
// component would have given the Board a gauge that drifts from the VP page's
// on the next edit, and "close but not the same reads as a different product"
// is the reason these numbers were measured out of the old bundle in the first
// place.
//
// Every constant below came from that bundle rather than being matched by eye:
// centre (100,100), radius 75, stroke 14, the arc rotated 135° and spanning
// three quarters of the circle, the notch from radius 64 to 86 with its label
// at 96.
//
// How full the arc runs depends on which way the metric improves. Service
// level, efficiency and QA fill TOWARDS their target; speed of answer and
// handle time fill as they come UNDER it, so a fast answer is a full arc — and
// for those the notch sits at the end, because the whole arc is the goal.

const CX = 100;
const CY = 100;
const R = 75;
const STROKE = 14;
const CIRC = 2 * Math.PI * R;
const SPAN = CIRC * 0.75;                            // 270 degrees
const ROTATE = `rotate(135 ${CX} ${CY})`;
const NOTCH_IN = R - STROKE / 2 - 4;
const NOTCH_OUT = R + STROKE / 2 + 4;

export const TONE = { good: '#059669', amber: '#d97706', bad: '#dc2626', neutral: '#cbd5e1' };
const TRACK = '#e2e8f0';
const NOTCH = '#475569';

const clamp01 = (x) => Math.max(0, Math.min(1, x));

/** Where a fraction of the way round the arc lands, in SVG coordinates. */
function atFraction(fraction) {
  const angle = ((135 + 270 * clamp01(fraction)) * Math.PI) / 180;
  return {
    x1: CX + NOTCH_IN * Math.cos(angle), y1: CY + NOTCH_IN * Math.sin(angle),
    x2: CX + NOTCH_OUT * Math.cos(angle), y2: CY + NOTCH_OUT * Math.sin(angle),
    labelX: CX + (NOTCH_OUT + 10) * Math.cos(angle), labelY: CY + (NOTCH_OUT + 10) * Math.sin(angle),
  };
}

export default function ArcDial({ value, display, tone, fill, goalFraction, goalLabel, ariaLabel }) {
  const colour = value == null ? TONE.neutral : (TONE[tone] || TONE.neutral);
  const shown = clamp01(fill ?? 0);
  const notch = goalFraction == null ? null : atFraction(goalFraction);

  return (
    // Capped and centred, NOT free to fill its column. Every font inside the
    // arc is in viewBox units, so an uncapped SVG scales its own type with it:
    // in the Exec page's three-across grid the gauge reached ~660px and drew
    // "94.4%" at ~110px, with the sublabel running past the arc. The old app's
    // hero gauge caps itself the same way (`maxWidth: 212`, `margin: auto`),
    // which is why six-across and three-across look identical there.
    <div style={s.arcBox}>
      <svg viewBox="0 0 200 200" style={s.svg} preserveAspectRatio="xMidYMid meet" role="img"
        aria-label={ariaLabel}>
        <circle cx={CX} cy={CY} r={R} fill="none" stroke={TRACK} strokeWidth={STROKE}
          strokeLinecap="round" strokeDasharray={`${SPAN} ${CIRC - SPAN}`} transform={ROTATE} />
        <circle cx={CX} cy={CY} r={R} fill="none" stroke={colour} strokeWidth={STROKE}
          strokeLinecap="round" strokeDasharray={`${SPAN * shown} ${CIRC - SPAN * shown}`} transform={ROTATE} />
        {notch && (
          <>
            <line x1={notch.x1} y1={notch.y1} x2={notch.x2} y2={notch.y2}
              stroke={NOTCH} strokeWidth="3" strokeLinecap="round" />
            {goalLabel && (
              <text x={notch.labelX} y={notch.labelY} textAnchor="middle" dominantBaseline="central"
                fontSize="10" fontWeight="600" fill="#64748b">{goalLabel}</text>
            )}
          </>
        )}
        <text x={CX} y={CY - 4} textAnchor="middle" fontSize="34" fontWeight="700"
          fill={value == null ? '#94a3b8' : '#0f172a'} style={{ fontVariantNumeric: 'tabular-nums' }}>
          {display}
        </text>
      </svg>
    </div>
  );
}

/** How full the arc runs, by which direction the metric improves in. */
export const fillTowards = (value, goal) => (value == null || !(goal > 0) ? 0 : clamp01(value / goal));
export const fillUnder = (value, goal) => (value == null || !(goal > 0) ? 0 : clamp01(goal / Math.max(value, 1)));

const s = {
  arcBox: { width: '100%', maxWidth: '212px', margin: '0 auto', aspectRatio: '200 / 150' },
  svg:    { width: '100%', height: '100%' },
};
